import React from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../Header';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { ArrowLeft, Apple, Download, Terminal, KeyRound, Play, Clock } from 'lucide-react';

const Step = ({ n, title, children }) => (
  <div className="flex gap-3">
    <div className="w-6 h-6 rounded-full bg-[#0F62FE]/10 text-[#0F62FE] dark:text-[#5C97FF] text-[12px] font-semibold flex items-center justify-center shrink-0">{n}</div>
    <div>
      <div className="text-[13px] font-medium text-[#0A0A0A] dark:text-[#F5F5F5]">{title}</div>
      <div className="text-[12px] text-black/50 dark:text-white/50 mt-0.5">{children}</div>
    </div>
  </div>
);

export default function DeviceClientDownload() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#FAFAFA] dark:bg-[#050505]">
      <Header />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8" data-testid="download-client-page">
        <button onClick={() => navigate('/devices')} data-testid="back-to-devices"
          className="inline-flex items-center gap-1.5 text-[13px] text-black/50 dark:text-white/50 hover:text-[#0F62FE] dark:hover:text-[#5C97FF] mb-6 transition-colors">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to Devices
        </button>

        <div className="text-center mb-8">
          <div className="w-14 h-14 rounded-2xl bg-[#0F62FE]/10 flex items-center justify-center mx-auto mb-4">
            <Download className="w-7 h-7 text-[#0F62FE] dark:text-[#5C97FF]" />
          </div>
          <h1 className="text-2xl font-semibold text-[#0A0A0A] dark:text-[#F5F5F5]">Nova Device Client</h1>
          <p className="text-sm text-black/55 dark:text-white/55 mt-2 max-w-lg mx-auto">
            A lightweight app that reports this device's security posture to Nova. Download, enroll with a code,
            and run a check — no Terminal required.
          </p>
        </div>

        {/* Download card — placeholder */}
        <Card className="border-black/10 dark:border-white/10 mb-4">
          <CardContent className="p-5">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="w-10 h-10 rounded-lg bg-black/5 dark:bg-white/10 flex items-center justify-center">
                <Apple className="w-5 h-5 text-black/60 dark:text-white/70" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-[#0A0A0A] dark:text-[#F5F5F5]">macOS</div>
                <div className="text-[12px] text-black/45 dark:text-white/45">Universal (Apple Silicon + Intel) · signed & notarized</div>
              </div>
              <Button disabled data-testid="download-macos-btn"
                className="bg-[#0F62FE]/50 text-white cursor-not-allowed">
                <Clock className="w-4 h-4 mr-1.5" /> Coming soon
              </Button>
            </div>
            <p className="text-[11px] text-black/40 dark:text-white/40 mt-3 flex items-center gap-1.5">
              <Terminal className="w-3.5 h-3.5" /> The signed desktop client is in development. Windows and Linux will follow.
            </p>
          </CardContent>
        </Card>

        {/* How it works */}
        <Card className="border-black/10 dark:border-white/10">
          <CardContent className="p-5">
            <h2 className="text-sm font-semibold text-[#0A0A0A] dark:text-[#F5F5F5] mb-4">How enrollment works</h2>
            <div className="space-y-4">
              <Step n={1} title="Download & open the client">Install the Nova Device Client on the device you want to enroll.</Step>
              <Step n={2} title="Paste your enrollment code">Generate a code from the Devices page (Enroll Device) and paste it into the client.</Step>
              <Step n={3} title="Run Device Check">The client collects posture and submits it securely — the device appears on your Devices page immediately.</Step>
            </div>
            <div className="flex items-center gap-2 mt-5 pt-4 border-t border-black/5 dark:border-white/5">
              <Button variant="outline" onClick={() => navigate('/devices')} className="border-black/15 dark:border-white/15" data-testid="go-generate-code">
                <KeyRound className="w-4 h-4 mr-1.5" /> Generate an enrollment code
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
